import { FC, useEffect, useRef } from 'react';

import { isAccountStatusReadOnly } from 'utils/clickme';
import { CampaignType } from 'utils/constants/campaign';

import dashboardBannerButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_banner_button_click';
import dashboardBannerSwitcherButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_banner_switcher_button_click';
import { BottomSheet, Button, Card, Flex, Text, TooltipHover, useBreakpoint } from '@hh.ru/magritte-ui';
import {
    ChartBarOutlinedSize16,
    PauseFilledSize16,
    PenOutlinedSize16,
    PlayFilledSize16,
} from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { updateBannerStatus } from 'components/Banners/components/Banner/components/BannerActions/updateBannerStatus';

import { CampaignFrontendStatus } from 'store/models/campaignsDashboard';
import { useDispatch, useSelector } from 'store/utils';

interface BannerActionsProps {
    bannerId: number;
    campaignId: number;
    enabled: boolean;
    campaignStatus: CampaignFrontendStatus;
    advertisingServiceType: CampaignType;
    shows: number;
    hasOnlyOneEnabledBanner: boolean;
    singleBanner: boolean;
}

const TrlKeys = {
    pause: 'BannersPage.banner.actions.pause',
    resume: 'BannersPage.banner.actions.resume',
    edit: 'BannersPage.banner.actions.edit',
    statistics: 'BannersPage.banner.actions.statistics',
    pauseTooltip: 'BannersPage.banner.actions.pause.tooltip',
};

const getGrowProps = (stretch: boolean): { grow?: number; basis?: number } => (stretch ? { grow: 1, basis: 0 } : {});

export const BannerActions: FC<BannerActionsProps> = ({
    bannerId,
    campaignId,
    enabled,
    campaignStatus,
    advertisingServiceType,
    shows,
    hasOnlyOneEnabledBanner,
    singleBanner,
}) => {
    const [tooltipVisible, setTooltipVisibleOn, setTooltipVisibleOff] = useOnOffState(false);
    const [isStatusChanging, setIsStatusChanging, setIsStatusChangingOff] = useOnOffState(false);

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const dispatch = useDispatch();
    const switchButtonRef = useRef<HTMLDivElement>(null);
    const { abortRequest, abortSignal } = useRequestCancel();

    const currentAccountStatus = useSelector(({ user }) => user.user?.currentAccount.status);

    useEffect(() => {
        return () => {
            abortRequest();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isArchivedCampaign = campaignStatus === CampaignFrontendStatus.Archived;
    const isBlockedCampaign = campaignStatus === CampaignFrontendStatus.Blocked;
    const isBudgetExceededCampaign =
        campaignStatus === CampaignFrontendStatus.DailyBudgetExceedStatus ||
        campaignStatus === CampaignFrontendStatus.BudgetExceeded;
    const isInformationCampaign = advertisingServiceType === CampaignType.Information;
    const isReadOnlyAccount = isAccountStatusReadOnly(currentAccountStatus);

    const showEditButton = isInformationCampaign && !isArchivedCampaign && !isReadOnlyAccount;

    const showSwitchButton = !(
        singleBanner ||
        isArchivedCampaign ||
        isBlockedCampaign ||
        isBudgetExceededCampaign ||
        isReadOnlyAccount
    );

    const stretchEditButton = !isMobile || !showSwitchButton;
    const stretchStatisticsButton = !isMobile || (!showSwitchButton && !showEditButton);

    const onChangeBannerStatus = () => {
        dashboardBannerSwitcherButtonClick({ activeStatus: String(enabled), bannerId });
        setIsStatusChanging();

        void dispatch(updateBannerStatus(bannerId, enabled, abortSignal())).finally(() => {
            setIsStatusChangingOff();
        });
    };

    if (shows === 0 && !showEditButton && !showSwitchButton) {
        return null;
    }

    return (
        <>
            <Card.Between />
            <Card.Segment p={12}>
                <Flex direction="row" gap={10}>
                    {showSwitchButton && (
                        <Flex
                            ref={switchButtonRef}
                            grow={1}
                            basis={0}
                            onClick={() => {
                                if (hasOnlyOneEnabledBanner && enabled) {
                                    setTooltipVisibleOn();
                                }
                            }}
                        >
                            <Button
                                mode={enabled ? 'secondary' : 'primary'}
                                style="neutral"
                                icon={enabled ? <PauseFilledSize16 /> : <PlayFilledSize16 />}
                                onClick={onChangeBannerStatus}
                                spaceBetween={false}
                                stretched
                                loading={isStatusChanging}
                                disabled={hasOnlyOneEnabledBanner && enabled}
                                data-qa={`switch-banner-button-${enabled ? 'enabled' : 'disabled'}`}
                            >
                                {trls(enabled ? TrlKeys.pause : TrlKeys.resume)}
                            </Button>
                        </Flex>
                    )}
                    {showEditButton && (
                        <Flex {...getGrowProps(stretchEditButton)}>
                            <Button
                                mode="secondary"
                                style="neutral"
                                Element="a"
                                href={`/new/campaign/${campaignId}/banner/${bannerId}/`}
                                target="_blank"
                                onClick={() =>
                                    dashboardBannerButtonClick({
                                        buttonName: 'banner_edit_button',
                                        campaignId,
                                        bannerId,
                                    })
                                }
                                hideLabel={isMobile && showSwitchButton}
                                icon={<PenOutlinedSize16 />}
                                spaceBetween={false}
                                stretched={stretchEditButton}
                                data-qa="edit-banner-button"
                            >
                                {trls(TrlKeys.edit)}
                            </Button>
                        </Flex>
                    )}
                    {shows > 0 && (
                        <Flex {...getGrowProps(stretchStatisticsButton)}>
                            <Button
                                mode="secondary"
                                style="neutral"
                                Element="a"
                                href={`/new/banner/${bannerId}/statistics/`}
                                target="_blank"
                                onClick={() =>
                                    dashboardBannerButtonClick({
                                        buttonName: 'banner_statistics',
                                        campaignId,
                                        bannerId,
                                    })
                                }
                                icon={<ChartBarOutlinedSize16 />}
                                hideLabel={isMobile && showSwitchButton && showEditButton}
                                spaceBetween={false}
                                stretched={stretchStatisticsButton}
                                data-qa="statistics-banner-button"
                            >
                                {trls(TrlKeys.statistics)}
                            </Button>
                        </Flex>
                    )}
                </Flex>
                {showSwitchButton && enabled && hasOnlyOneEnabledBanner && (
                    <>
                        <TooltipHover activatorRef={switchButtonRef} placement="top-center">
                            {trls(TrlKeys.pauseTooltip)}
                        </TooltipHover>
                        <BottomSheet visible={tooltipVisible} onClose={setTooltipVisibleOff}>
                            <Text typography="label-2-regular">{trls(TrlKeys.pauseTooltip)}</Text>
                        </BottomSheet>
                    </>
                )}
            </Card.Segment>
        </>
    );
};
